import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Modal } from 'antd';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import type { CopyRepeatsResultDto, FillShareDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { DUTY_FILL_COPY } from '../copy';
import {
  COPY_REPEATS_ICON,
  COPY_REPEATS_LOOK,
  COPY_REPEATS_MIN_NAMED_PERCENT,
  copyRepeatsGroups,
  copyRepeatsOffer,
  copyRepeatsTitle,
} from '../copyRepeats';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [COPY-REPEATS] COPY TO OTHER DATES — ONE BUTTON, ONE WRITE, TWO MODALS.
 *
 *  ⚠️ THE BUTTON IS ALWAYS CLICKABLE; THE FIRST MODAL EXPLAINS. Below 80% of the footer's own figure,
 *  for a role that cannot name by hand, or on a sheet spanning several divisions, it says why and
 *  offers Cancel alone — never a disabled button an operator has to guess about. At or above, it says
 *  what is replaced and what is skipped, and offers Confirm.
 *
 *  ⚠️ THE SECOND MODAL IS THE SERVER'S ANSWER, EVERY DATE IT REACHED. It stays until dismissed, because
 *  a toast is gone before anybody has read five dates.
 *
 *  ⚠️ IT OWNS ITS BUTTON, ITS DIALOGS AND ITS WRITE — `UnpublishAction`'s shape — so the page's own
 *  census count does not move. Its two `<Modal>`s are counted against THIS file in
 *  `e2e/fixtures/overlays.ts`.
 *
 *  ⚠️ THE WRITE TOUCHES NO LIVE SHEET. The copy writes OTHER duties, so this page's sheet is unchanged
 *  and is not re-read; the page's `onCopied` flushes the counts two screens away (the inbox, the lists,
 *  this order's record), and each copied date's cached sheet is REMOVED — never invalidated, and never
 *  this page's — so opening that date reads it fresh rather than from a cache `staleTime: Infinity`
 *  keeps for ever.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const CopyToOtherDates = ({
  dutyId,
  division,
  named,
  total,
  refusal,
  dutyPointTitle,
  shares,
  onCopied,
}: {
  dutyId: string;
  /** The page's `?division=`, sent as the server's narrowing — `notify`'s and `completions`' idiom. */
  division: string | undefined;
  /** ⚠️ THE FOOTER'S OWN `named` AND `total` — the figures `fillFooter` is given, never a second count. */
  named: number;
  total: number;
  /** The capability's refusal when the caller cannot name by hand; `undefined` when they can. */
  refusal: string | undefined;
  dutyPointTitle: string;
  /** The sheet's shares: how many divisions it spans, and each addressee's name for the answer. */
  shares: readonly FillShareDto[];
  onCopied: () => void;
}) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<CopyRepeatsResultDto | null>(null);

  const offer = copyRepeatsOffer({
    refusal,
    divisions: new Set(shares.flatMap((share) => (share.division ? [share.division.id] : []))).size,
    named,
    total,
  });

  const copy = useMutation({
    mutationFn: () =>
      api.post<CopyRepeatsResultDto>(
        `/duties/${dutyId}/copy-to-repeats`,
        division ? { divisionOrgUnitId: division } : {},
      ),
    onSuccess: (result) => {
      for (const target of result.targets) {
        if (target.skipped === null) queryClient.removeQueries({ queryKey: queryKeys.fill.sheet(target.deploymentId) });
      }
      onCopied();
      setAsking(false);
      setAnswer(result);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const nameOf = (orgUnitId: string): string | null =>
    shares.find((share) => share.orgUnit?.id === orgUnitId)?.orgUnit?.name ?? null;
  const close = (): void => setAsking(false);

  return (
    <>
      <Button
        className={COPY_REPEATS_LOOK}
        icon={<Copy className={COPY_REPEATS_ICON} aria-hidden />}
        aria-haspopup="dialog"
        data-testid="copy-repeats"
        onClick={() => setAsking(true)}
      >
        {DUTY_FILL_COPY.copyRepeats}
      </Button>

      <Modal
        open={asking}
        title={DUTY_FILL_COPY.copyRepeatsTitle}
        onCancel={close}
        data-testid="copy-repeats-modal"
        footer={[
          <Button key="cancel" data-testid="copy-repeats-cancel" onClick={close}>
            {DUTY_FILL_COPY.copyRepeatsCancel}
          </Button>,
          ...(offer.kind === 'READY'
            ? [
                <Button
                  key="confirm"
                  type="primary"
                  data-testid="copy-repeats-confirm"
                  loading={copy.isPending}
                  onClick={() => copy.mutate()}
                >
                  {DUTY_FILL_COPY.copyRepeatsConfirm}
                </Button>,
              ]
            : []),
        ]}
      >
        <div className="flex flex-col gap-2 text-sm" data-testid="copy-repeats-body" data-offer={offer.kind}>
          {offer.kind === 'READY' ? (
            DUTY_FILL_COPY.copyRepeatsReady(dutyPointTitle, named, total).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))
          ) : offer.kind === 'BELOW' ? (
            <p>{DUTY_FILL_COPY.copyRepeatsBelow(COPY_REPEATS_MIN_NAMED_PERCENT, named, total, offer.needed)}</p>
          ) : (
            <p>{offer.sentence}</p>
          )}
        </div>
      </Modal>

      <Modal
        open={answer !== null}
        title={answer ? copyRepeatsTitle(answer) : undefined}
        onCancel={() => setAnswer(null)}
        data-testid="copy-repeats-result"
        footer={[
          <Button key="done" type="primary" onClick={() => setAnswer(null)}>
            {DUTY_FILL_COPY.copyRepeatsDone}
          </Button>,
        ]}
      >
        {answer ? (
          <div className="flex flex-col gap-3 text-sm">
            {answer.targets.length === 0 ? <p>{DUTY_FILL_COPY.copyRepeatsNoDates(dutyPointTitle)}</p> : null}
            {copyRepeatsGroups(answer, nameOf).map((group) => (
              <section key={group.key} data-testid={`copy-repeats-${group.key}`}>
                <h3 className="font-medium">{group.heading}</h3>
                <ul className="mt-1 flex flex-col gap-0.5 text-muted-foreground">
                  {group.rows.map((row) => (
                    <li key={row}>{row}</li>
                  ))}
                </ul>
              </section>
            ))}
            <p className="text-muted-foreground">{DUTY_FILL_COPY.copyRepeatsNobodyTold}</p>
          </div>
        ) : null}
      </Modal>
    </>
  );
};
